import { _get } from './get'

const isObjectLike = (value) => typeof value === 'object' && value !== null

const isPlainObject = (value) => {
  if (!isObjectLike(value)) {
    return false
  }

  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

const cloneDescriptorValue = (value, seen) => {
  if (!isObjectLike(value)) {
    return value
  }

  return _cloneDeep(value, seen)
}

export const _cloneDeep = (value, seen = new WeakMap()) => {
  if (!isObjectLike(value)) {
    return value
  }

  if (seen.has(value)) {
    return seen.get(value)
  }

  if (value instanceof Date) {
    return new Date(value.getTime())
  }

  if (value instanceof RegExp) {
    return new RegExp(value.source, value.flags)
  }

  if (value instanceof Map) {
    const clonedMap = new Map()
    seen.set(value, clonedMap)
    value.forEach((entryValue, key) => {
      clonedMap.set(cloneDescriptorValue(key, seen), cloneDescriptorValue(entryValue, seen))
    })
    return clonedMap
  }

  if (value instanceof Set) {
    const clonedSet = new Set()
    seen.set(value, clonedSet)
    value.forEach((entryValue) => {
      clonedSet.add(cloneDescriptorValue(entryValue, seen))
    })
    return clonedSet
  }

  if (Array.isArray(value)) {
    const clonedArray = []
    seen.set(value, clonedArray)
    value.forEach((item, index) => {
      clonedArray[index] = cloneDescriptorValue(item, seen)
    })
    return clonedArray
  }

  const clonedObject = Object.create(Object.getPrototypeOf(value))
  seen.set(value, clonedObject)

  Reflect.ownKeys(value).forEach((key) => {
    const descriptor = Object.getOwnPropertyDescriptor(value, key)

    if (!descriptor) {
      return
    }

    if ('value' in descriptor) {
      descriptor.value = cloneDescriptorValue(descriptor.value, seen)
    }

    Object.defineProperty(clonedObject, key, descriptor)
  })

  return clonedObject
}

export const _isEmpty = (value) => {
  if (value == null) {
    return true
  }

  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length === 0
  }

  if (value instanceof Map || value instanceof Set) {
    return value.size === 0
  }

  if (isObjectLike(value)) {
    return Reflect.ownKeys(value).length === 0
  }

  return true
}

export const chunk = (array, size = 1) => {
  if (!Array.isArray(array) || size < 1) {
    return []
  }

  const chunks = []

  for (let index = 0; index < array.length; index += size) {
    chunks.push(array.slice(index, index + size))
  }

  return chunks
}

const mergeValue = (target, source) => {
  if (!isObjectLike(source)) {
    return source
  }

  if (Array.isArray(source)) {
    const baseArray = Array.isArray(target) ? target.slice() : []

    source.forEach((item, index) => {
      if (isPlainObject(item) || Array.isArray(item)) {
        baseArray[index] = mergeValue(baseArray[index], item)
      } else {
        baseArray[index] = _cloneDeep(item)
      }
    })

    return baseArray
  }

  if (!isPlainObject(source)) {
    return _cloneDeep(source)
  }

  const baseObject = isPlainObject(target) ? { ...target } : {}

  Reflect.ownKeys(source).forEach((key) => {
    const sourceValue = source[key]
    const targetValue = baseObject[key]

    if (isPlainObject(sourceValue) || Array.isArray(sourceValue)) {
      baseObject[key] = mergeValue(targetValue, sourceValue)
      return
    }

    baseObject[key] = _cloneDeep(sourceValue)
  })

  return baseObject
}

export const merge = (object, ...sources) => {
  return sources.reduce((result, source) => mergeValue(result, source), _cloneDeep(object))
}

const sameValueZero = (left, right) => left === right || (left !== left && right !== right)

export const isEqual = (left, right, seen = new WeakMap()) => {
  if (sameValueZero(left, right)) {
    return true
  }

  if (!isObjectLike(left) || !isObjectLike(right)) {
    return false
  }

  if (Object.getPrototypeOf(left) !== Object.getPrototypeOf(right)) {
    return false
  }

  if (seen.get(left) === right) {
    return true
  }

  seen.set(left, right)

  if (left instanceof Date) {
    return left.getTime() === right.getTime()
  }

  if (left instanceof RegExp) {
    return left.source === right.source && left.flags === right.flags
  }

  if (left instanceof Map) {
    if (left.size !== right.size) {
      return false
    }

    for (const [key, value] of left.entries()) {
      if (!right.has(key) || !isEqual(value, right.get(key), seen)) {
        return false
      }
    }

    return true
  }

  if (left instanceof Set) {
    if (left.size !== right.size) {
      return false
    }

    for (const value of left.values()) {
      if (!right.has(value)) {
        return false
      }
    }

    return true
  }

  if (Array.isArray(left)) {
    if (!Array.isArray(right) || left.length !== right.length) {
      return false
    }

    for (let index = 0; index < left.length; index += 1) {
      if (!isEqual(left[index], right[index], seen)) {
        return false
      }
    }

    return true
  }

  const leftKeys = Reflect.ownKeys(left)
  const rightKeys = Reflect.ownKeys(right)

  if (leftKeys.length !== rightKeys.length) {
    return false
  }

  return leftKeys.every((key) => {
    if (!Object.prototype.hasOwnProperty.call(right, key)) {
      return false
    }

    return isEqual(left[key], right[key], seen)
  })
}

export const _debounce = (func, wait = 0, options = {}) => {
  let timerId
  let lastArgs
  let lastCallTime
  let lastInvokeTime = 0
  let result
  let lastInvoke

  const leading = Boolean(options.leading)
  const trailing = options.trailing !== false
  const maxing = Number.isFinite(options.maxWait)
  const maxWait = maxing ? Math.max(Number(options.maxWait), wait) : 0

  const invokeFunc = () => {
    if (lastInvoke) {
      result = lastInvoke()
    }
    lastArgs = undefined
    lastInvoke = undefined
    return result
  }

  const shouldInvoke = (time) => {
    if (lastCallTime === undefined) {
      return true
    }

    const sinceLastCall = time - lastCallTime
    const sinceLastInvoke = time - lastInvokeTime

    return sinceLastCall >= wait || sinceLastCall < 0 || (maxing && sinceLastInvoke >= maxWait)
  }

  const remainingWait = (time) => {
    const sinceLastCall = time - lastCallTime
    const sinceLastInvoke = time - lastInvokeTime
    const waitRemaining = wait - sinceLastCall

    return maxing ? Math.min(waitRemaining, maxWait - sinceLastInvoke) : waitRemaining
  }

  const trailingEdge = (time) => {
    timerId = undefined

    if (trailing && lastArgs) {
      lastInvokeTime = time
      return invokeFunc()
    }

    lastArgs = undefined
    lastInvoke = undefined
    return result
  }

  const timerExpired = () => {
    const now = Date.now()

    if (shouldInvoke(now)) {
      trailingEdge(now)
      return
    }

    timerId = setTimeout(timerExpired, remainingWait(now))
  }

  const debounced = function (...args) {
    const now = Date.now()
    const isInvoking = shouldInvoke(now)

    lastArgs = args
    lastInvoke = () => func.apply(this, lastArgs)
    lastCallTime = now

    if (isInvoking) {
      if (timerId === undefined) {
        if (leading) {
          lastInvokeTime = now
          return invokeFunc()
        }

        timerId = setTimeout(timerExpired, wait)
        return result
      }

      if (maxing) {
        clearTimeout(timerId)
        timerId = setTimeout(timerExpired, wait)
        lastInvokeTime = now
        return invokeFunc()
      }
    }

    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait)
    }

    return result
  }

  debounced.cancel = () => {
    if (timerId !== undefined) {
      clearTimeout(timerId)
    }

    timerId = undefined
    lastArgs = undefined
    lastInvoke = undefined
    lastCallTime = undefined
    lastInvokeTime = 0
  }

  debounced.flush = () => {
    return timerId === undefined ? result : trailingEdge(Date.now())
  }

  return debounced
}

export { _get }
